import jwt from './jwt';

/**
 * Class to manage JWT refreshes
 */
export default class {
  /**
   * Constructor for JWT manager
   *
   * @param {Object} args specifying behaviour for JWT manager
   *             Params:
   *               refreshHandler:      A handler needs to be specified to perform HTTP transactions
   *                                    using the application HTTP library
   *                                    Should accept a boolean parameter that specified whether the
   *                                    refresh token needs to be refreshed in call
   *                                    Should return a promise containing the JSON response
   *                                    from the server
   *                                    If an exception occurs it should be thrown so that
   *                                    refreshErrorHandler can be called at the correct point in
   *                                    promise chain
   *               refreshErrorHandler: Handler for when an exception occurs during refresh
   *                                    Thrown exception will be passed to callback as parameter
   *               accessKey:           Name of localstorage key that stores the JWT access token
   *               refreshKey:          Name of localstorage key that stores the JWT refresh token
   *               refreshBuffer:       Time in s that a token refresh should occur before token
   *                                    expiry
   *               refreshOnly:         If true only refresh token will be refreshed - not access
   *                                    token
   */
  constructor(args = {}) {
    if (!('refreshHandler' in args)) {
      throw new Error('Refresh handler must be specified');
    }
    if (!('refreshErrorHandler' in args)) {
      throw new Error('Refresh error handler must be specified');
    }

    this._accessKey = args.accessKey || 'accessToken';
    this._refreshKey = args.refreshKey || 'refreshToken';
    this._refreshBuffer = ('refreshBuffer' in args) ? args.refreshBuffer : 60;
    this._refreshOnly = ('refreshOnly' in args) ? args.refreshOnly : false;
    this._refreshHandler = args.refreshHandler;
    this._refreshErrorHandler = args.refreshErrorHandler;

    this._refreshTimeout = null;
    this._isInitialized = false;
  }

  /**
   * Start the management loop for performing JWT refresh
   *
   * @return {*} Promise which resolves immediately if loop is running, or after first refresh is
   *             performed if it is not
   */
  start() {
    return Promise.resolve().then(() => {
      if (!this._isInitialized) {
        this._isInitialized = true;

        return this._readyTokenRefresh();
      }
    });
  }

  /**
   * Stop the JWT refresh loop
   */
  stop() {
    if (this._refreshTimeout) {
      clearTimeout(this._refreshTimeout);
      this._refreshTimeout = null;
    }
    this._isInitialized = false;
  }

  /**
   * Update refreshOnly status of manager
   *
   * @param {Boolean} refreshOnly New value of refreshOnly to set
   */
  refreshOnly(refreshOnly) {
    if (refreshOnly === this._refreshOnly) {
      return;
    }

    const startAgain = this._isInitialized;
    this.stop();
    this._refreshOnly = refreshOnly;

    if (startAgain) {
      this.start();
    }
  }

  /**
   * Perform a token refresh
   * Calls refreshHandler to obtain new JWT token/s then queues the next refresh
   * If refreshOnly = true then the access token will not be stored
   * Note that the refresh loop will be broken if an exception occurs (must be restarted)
   *
   * @param {Boolean} withRefresh Whether to refresh the refresh token as well as access token
   *                  (propagated to refreshOnly)
   * @return {*} Promise which resolves when refresh has completed
   */
  doRefresh(withRefresh = false) {
    this._isInitialized = true;

    return this._refresh(this._refreshHandler(withRefresh), withRefresh);
  }

  /**
   * Actually perform a refresh
   * Waits for promise then stores result and initiates next refresh call
   *
   * @param {Promise} refreshRequest  Promise that will resolve with the response from server
   * @param {Boolean} withRefresh     Whether refresh response should contain new refresh token
   * @return {Promise<T>}  Promise which resolves once refresh is complete
   * @private
   */
  _refresh(refreshRequest, withRefresh) {
    if (this._refreshTimeout) {
      clearTimeout(this._refreshTimeout);
      this._refreshTimeout = null;
    }

    return refreshRequest.then((res) => {
      if (!this._refreshOnly) {
        localStorage.setItem(this._accessKey, res.data.accessToken);
      }
      if (withRefresh) {
        localStorage.setItem(this._refreshKey, res.data.refreshToken);
      }

      this._readyTokenRefresh();
    }).catch((err) => { // Loop is no longer being executed
      this._isInitialized = false;

      throw err;
    }).catch(this._refreshErrorHandler);
  }

  /**
   * Indicate whether the stored refresh token will need to be refreshed as part of the next refresh
   * call
   *
   * @return {Boolean} True if refresh token should be refreshed
   */
  refreshRequiresRefresh() {
    let token = localStorage.getItem(this._accessKey);
    let refreshToken = localStorage.getItem(this._refreshKey);
    if (!token || !refreshToken) {
      return true;
    }

    token = jwt.parse(localStorage.getItem(this._accessKey));
    refreshToken = jwt.parse(localStorage.getItem(this._refreshKey));
    const refreshIn = token.exp * 1000 - Date.now() - this._refreshBuffer * 1000;

    return refreshToken.exp * 1000 - Date.now() - this._refreshBuffer * 1000 < refreshIn * 2;
  }

  /**
   * Create a timeout to perform next refresh
   *
   * @return {Promise<any>} resolves when queued refresh completes
   * @private
   */
  _readyTokenRefresh() {
    let token;
    if (!this._refreshOnly) {
      token = jwt.parse(localStorage.getItem(this._accessKey));
    } else {
      token = jwt.parse(localStorage.getItem(this._refreshKey));
    }
    let refreshIn;
    let getRefreshToken;
    refreshIn = token.exp * 1000 - Date.now() - this._refreshBuffer * 1000;
    getRefreshToken = this._refreshOnly || this.refreshRequiresRefresh();

    return new Promise((res, fail) => {
      this._refreshTimeout = setTimeout(() => {
        this._refreshTimeout = null;
        res();
      }, refreshIn);
    }).then(() => {
      return this.doRefresh(getRefreshToken);
    });
  }
}
